import { keyframes } from '@emotion/react';
import { Theme, alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

export type RachelInputExpandDirection = 'up' | 'down';

const GLOW_DRIFT_DURATION_IN_SECONDS = 6;

export const PILL_COMPACT_PADDING_X = 4;

export const PILL_COLUMN_GAP = 4;

export const CONTEXT_CHIP_HEIGHT = 28;

export const CONTEXT_CHIP_MARGIN = 4;

export const CONTEXT_CHIP_ROW_HEIGHT = CONTEXT_CHIP_HEIGHT + CONTEXT_CHIP_MARGIN;

const ATTACHMENTS_SCROLLBAR_HEIGHT = 4;

const ATTACHMENTS_ROW_PADDING = 0.5;

const GLOW_OPACITY = 0.2;

const OUTLINE_OPACITY = 0.25;

const glowDrift = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

interface RachelInputStylesParams {
  minHeight: number;
  isContextChipSpaceReserved: boolean;
  expandDirection: RachelInputExpandDirection;
  isActive: boolean;
  isTyping: boolean;
  isVertical: boolean;
  isMerged: boolean;
  isRound: boolean;
  isSparkInteractive: boolean;
}

const STACK_EXPANDING_DOWN = { display: 'grid' as const };

const STACK_EXPANDING_UP = {
  display: 'grid' as const,
  position: 'absolute' as const,
  bottom: 0,
  left: 0,
  right: 0,
};

const getCardSurface = (theme: Theme, isHighlighted: boolean) => ({
  background: isHighlighted ? theme.palette.common.white : theme.palette.secondary.main,
  border: `1px solid ${isHighlighted ? 'transparent' : theme.palette.custom.border.primary}`,
});

interface SurfaceBorderRadiusParams {
  isRound: boolean;
  isMerged: boolean;
}

const getSurfaceBorderRadius = (theme: Theme, { isRound, isMerged }: SurfaceBorderRadiusParams) => {
  if (isRound) {
    return theme.shape.borderRadiusFull;
  }

  return isMerged ? theme.shape.borderRadiusXl : theme.shape.borderRadiusLg;
};

const getActionButtonBorderRadius = (isRound: boolean) => (isRound ? '50%' : 6);

const getSparkColor = (theme: Theme, isTyping: boolean) =>
  isTyping ? theme.palette.accent.light : theme.palette.primary.main;

const getLayerOpacity = (isVisible: boolean, opacity: number) => (isVisible ? opacity : 0);

const getPillLayout = (isVertical: boolean) =>
  isVertical
    ? {
        gridTemplateColumns: '1fr auto',
        gridTemplateAreas: '"input input" "spark actions"',
        padding: '6px',
      }
    : {
        gridTemplateColumns: 'auto 1fr auto',
        gridTemplateAreas: '"spark input actions"',
        padding: `1px ${PILL_COMPACT_PADDING_X}px`,
      };

export const useRachelInputStyles = makeStyles<
  RachelInputStylesParams,
  'sparkStroke' | 'iconButton' | 'actionButton'
>()(
  (
    theme,
    {
      minHeight,
      isContextChipSpaceReserved,
      expandDirection,
      isActive,
      isTyping,
      isVertical,
      isMerged,
      isRound,
      isSparkInteractive,
    },
    classes,
  ) => {
    const isHighlighted = isActive || isMerged;
    const surfaceBorderRadius = getSurfaceBorderRadius(theme, { isRound, isMerged });

    const sparkHoverFrom = (target: string) =>
      [classes.sparkStroke, classes.iconButton, classes.actionButton]
        .map((triggerClass) => `&:has(.${triggerClass}:hover) .${classes.sparkStroke}${target}`)
        .join(', ');

    return {
      anchor: {
        position: 'relative',
        zIndex: theme.zIndex.drawer,
        width: '100%',
        minHeight: isContextChipSpaceReserved ? minHeight + CONTEXT_CHIP_ROW_HEIGHT : minHeight,
      },
      stack: expandDirection === 'up' ? STACK_EXPANDING_UP : STACK_EXPANDING_DOWN,
      glow: {
        gridArea: '1 / 1',
        borderRadius: surfaceBorderRadius,
        filter: 'blur(4px)',
        backgroundImage: theme.palette.custom.gradients.rachelGlow,
        backgroundSize: '200% 100%',
        backgroundPosition: '0% 50%',
        opacity: getLayerOpacity(isHighlighted, GLOW_OPACITY),
        animation: isHighlighted
          ? `${glowDrift} ${GLOW_DRIFT_DURATION_IN_SECONDS}s ease-in-out infinite alternate`
          : undefined,
        transition: 'opacity .15s',
        pointerEvents: 'none',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      },
      card: {
        gridArea: '1 / 1',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: surfaceBorderRadius,
        overflow: 'hidden',
        transition: 'background .15s, border-color .15s',
        ...getCardSurface(theme, isHighlighted),
      },
      outline: {
        gridArea: '1 / 1',
        position: 'relative',
        zIndex: 2,
        padding: 1,
        borderRadius: surfaceBorderRadius,
        backgroundImage: theme.palette.custom.gradients.rachelGlow,
        opacity: getLayerOpacity(isHighlighted, OUTLINE_OPACITY),
        transition: 'opacity .15s',
        pointerEvents: 'none',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
      },
      attachmentsRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(
          ATTACHMENTS_ROW_PADDING,
          ATTACHMENTS_ROW_PADDING,
          ATTACHMENTS_ROW_PADDING,
        ),
        overflowX: 'auto',
        overflowY: 'hidden',
        overscrollBehaviorX: 'contain',
        scrollBehavior: 'smooth',
        '@media (prefers-reduced-motion: reduce)': {
          scrollBehavior: 'auto',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&:hover': {
          scrollbarColor: `${theme.palette.action.active} transparent`,
        },
        '&::-webkit-scrollbar': {
          height: ATTACHMENTS_SCROLLBAR_HEIGHT,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: theme.shape.borderRadiusFull,
        },
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.action.active,
        },
      },
      pill: {
        display: 'grid',
        alignItems: 'center',
        columnGap: `${PILL_COLUMN_GAP}px`,
        maxWidth: '100%',
        minHeight,
        ...getPillLayout(isVertical),
        ...(isSparkInteractive && {
          [sparkHoverFrom('')]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          [sparkHoverFrom(' svg')]: {
            transform: 'scale(0.9)',
          },
        }),
      },
      sparkSlot: {
        gridArea: 'spark',
        display: 'inline-flex',
        alignItems: 'center',
      },
      sparkStroke: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        flex: 'none',
        width: 28,
        height: 28,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: getSparkColor(theme, isTyping),
        cursor: isSparkInteractive ? 'pointer' : 'default',
        borderRadius: theme.shape.borderRadiusMd,
        transition: 'background-color .15s, color .15s',
        '& svg': {
          transition: 'transform .15s',
          transformOrigin: 'center',
        },
      },
      input: {
        gridArea: 'input',
        fontSize: 13,
        lineHeight: 1.4,
        color: theme.palette.text.primary,
        padding: isVertical ? '2px' : 0,
        '& input, & textarea': {
          padding: 0,
        },
      },
      actionsGroup: {
        gridArea: 'actions',
        justifySelf: 'end',
        display: 'flex',
        alignItems: 'center',
      },
      iconButton: {
        '&.MuiIconButton-root': {
          width: 28,
          height: 28,
          padding: 0,
          flex: 'none',
          borderRadius: 6,
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
      actionButton: {
        '&.MuiIconButton-root': {
          width: 22,
          height: 22,
          padding: 0,
          flex: 'none',
          margin: 3,
          borderRadius: getActionButtonBorderRadius(isRound),
          color: theme.palette.primary.dark,
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.35),
          },
        },
      },
      stopSquare: {
        width: 8,
        height: 8,
        borderRadius: 2,
        backgroundColor: theme.palette.primary.dark,
      },
    };
  },
);
